import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { BiChat, BiFlag, BiInfoCircle, BiTrash, BiWrench } from 'react-icons/bi';
import { FaRegHandshake } from 'react-icons/fa6';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import z from 'zod';

import { useVhAdminAccess } from '@/admin/useVhAdminAccess';
import { getServiceId } from '@/api/tokens.helper.ts';
import { imageVariantLink } from '@/common/media';
import { ConfirmationModal, Modal, SplitLayout, useSplitFolded } from '@/components';
import { BasicLayout, BasicLayoutContent } from '@/components/BasicLayouts/BasicLayouts';
import { Breadcrumb } from '@/components/Breadcrumb/Breadcrumb';
import { Button, ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import type { TaskSubmissionPayload } from '@/components/CreateSheet/CreateEditTaskSheet';
import CreateEditTaskSheet from '@/components/CreateSheet/CreateEditTaskSheet';
import { DocumentPreviewModal } from '@/components/DocumentPreview/DocumentPreview';
import { Header } from '@/components/Header';
import { docStyles as d, ProfileIdentity, ShortlistButton } from '@/components/ProfileDoc';
import { ProfileNav } from '@/components/ProfileLayout';
import { RichTextArea } from '@/components/RichTextArea/RichTextArea';
import { StatusPill } from '@/components/StatusPill/StatusPill';
import { useIsServiceProvider } from '@/hooks/useIsServiceProvider';
import { useAuth } from '@/store/auth/auth.store';
import { useChats } from '@/store/chats/chats.store.ts';
import { isOpenProviderReport } from '@/store/provider-reports/provider-reports.mappers';
import {
  useAdminProviderReportsQuery,
  useMyProviderReportsQuery,
} from '@/store/provider-reports/provider-reports.store';
import {
  ServiceProviderCreatedBy,
  type ServiceProviderImage,
} from '@/store/service-providers/service-providers.dto';
import {
  useDeleteServiceProviderMutation,
  useServiceProviderDeleteNoteMutation,
  useServiceProvidersQuery,
  useServiceProviderUpdateFavoriteMutation,
  useServiceProviderUpdateNoteMutation,
  useSingleServiceProviderQuery,
} from '@/store/service-providers/service-providers.store';
import { useTasks } from '@/store/tasks/tasks.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { flattenInfinitePages } from '@/utils/list';
import { TASK_ID_SEARCH_PARAM, taskHref } from '@/utils/task-href';
import { toast } from '@/utils/toast';

import { DISCOVER_PROVIDERS_ROUTE } from '../Discover/constants';
import { useDiscoverRecordBreadcrumb } from '../Discover/useDiscoverBreadcrumb';
import { SERVICE_HISTORY_ROUTE } from '../Maintain/constants';

import { providerAddress, providerCategoryTitles, providerDistance } from './provider-facts';
import {
  RecordContactFacts,
  RecordContactList,
  RecordLocations,
  RecordPhotos,
  RecordSection,
  RecordServices,
  RecordText,
} from './ProviderRecord';
import { ProviderReportsTab, ReportProviderModal } from './ProviderReports';
import { CreateUpdateServiceProvider } from './ServiceProviderForms';
import s from './ServiceProviders.module.scss';

export const SERVICE_PROVIDER_ASSIGN_TO_SEARCH_KEY = 'assign_to';
export const PORT_LATITUDE_SEARCH_KEY = 'port_latitude';
export const PORT_LONGTITUDE_SEARCH_KEY = 'port_longtitude';
export const EQUIPMENT_ID_SEARCH_KEY = 'equipment_id';
export const EQUIPMENT_NAME_SEARCH_KEY = 'equipment_name';

/** What the desktop catalogue opens on, so the switcher reuses its cached page. */
const PROVIDER_SWITCHER_LIMIT = 12;

/**
 * A service provider as the document it is, on the shell the crew CV uses: who
 * they are and what you can do about them in the aside, the record itself as a
 * column of cards beside it — about, services, where they work from, how to
 * reach them, their work, and whatever this vessel wrote about them.
 *
 * Only two things here can be written to, and each carries the Edit that writes
 * it: the record behind one form (the same modal the catalogue creates with), and
 * the note, which is this vessel's own and has an endpoint of its own. The rest
 * of the cards are read-only for everyone, which is why they carry no Edit each.
 */
const SingleServiceProvider = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { createEmailChatRoom, getChatRooms } = useChats();
  const { isServiceProvider } = useIsServiceProvider();
  const folded = useSplitFolded();
  const session = useAuth((state) => state.session);
  const { vessel } = useVessels();

  const [openedModal, setOpenedModal] = useState<OpenedModal>(OpenedModal.NONE);
  const [previewImage, setPreviewImage] = useState<ServiceProviderImage | null>(null);
  const [tab, setTab] = useState<ProviderTab>('info');

  const portGeolocation =
    searchParams.has(PORT_LATITUDE_SEARCH_KEY) && searchParams.has(PORT_LONGTITUDE_SEARCH_KEY)
      ? {
          latitude: +searchParams.get(PORT_LATITUDE_SEARCH_KEY)!,
          longitude: +searchParams.get(PORT_LONGTITUDE_SEARCH_KEY)!,
        }
      : null;

  const { data: serviceProvider } = useSingleServiceProviderQuery({
    id: params.id!,
    vesselId: getServiceId(),
    ...portGeolocation,
  });

  // The header's switcher: the catalogue's own first page, so arriving from the
  // list needs no request of its own.
  const { data: providers } = useServiceProvidersQuery(PROVIDER_SWITCHER_LIMIT, {
    search: '',
    vesselId: getServiceId(),
  });
  const breadcrumb = useDiscoverRecordBreadcrumb({
    listRoute: DISCOVER_PROVIDERS_ROUTE,
    recordPath: (providerId) => `/service-providers/${providerId}`,
    record: { id: params.id!, label: serviceProvider?.name ?? 'Service provider' },
    peers: flattenInfinitePages(providers?.pages).map(({ id, name }) => ({ id, label: name })),
  });

  const { mutateAsync: setIsFavorite, isPending: isSetIsFavoritePending } =
    useServiceProviderUpdateFavoriteMutation();
  const { mutateAsync: deleteServiceProvider, isPending: isDeleting } =
    useDeleteServiceProviderMutation();
  const isPlatformAdmin = useVhAdminAccess() === 'allowed';
  const { data: adminReports = [] } = useAdminProviderReportsQuery(params.id, isPlatformAdmin);

  const handleDelete = async () => {
    try {
      await deleteServiceProvider(params.id!);
      toast.success('Service provider deleted');
      navigate('/service-providers');
    } catch (err: unknown) {
      console.error(err);
      toast.error('Couldn’t delete the service provider. Please try again.');
    }
  };

  const handleCreateChat = async () => {
    const chatRooms = await getChatRooms();
    if (!serviceProvider) {
      return;
    }
    const room = chatRooms.find(
      ({ type, users }) => type === 'email' && users?.some(({ id }) => id === serviceProvider.id),
    );
    let roomId = room?.id;
    if (!room) {
      const { id } = await createEmailChatRoom(
        serviceProvider.id,
        vessel?.vessel_name
          ? `${vessel.vessel_name} - ${serviceProvider.name} Chat`
          : `Chat with ${serviceProvider.name}`,
      );
      roomId = id;
    }
    navigate(`/inbox/${roomId}`);
  };

  // Mirrors SPCreatorMiddleware (`creator_id == user_id || creator_id == service_id`), which
  // gates PUT/DELETE server-side. `creator_id` holds the *vessel's* service id for a
  // catalogue entry and the *person's* id for an SP account, so one pair covers both.
  // Keying on `created_by === vessel` instead offered Edit on other vessels' entries — the
  // button opened the form and the save came back 403.
  const canModify =
    !!serviceProvider?.creator_id &&
    (serviceProvider.creator_id === getServiceId() || serviceProvider.creator_id === session?.id);
  // A platform admin reads every report on the provider; anyone else, where they
  // can report at all, the ones they filed. Nobody reports a record they can delete.
  const canReport = !!serviceProvider && !canModify;
  const { data: myReports = [] } = useMyProviderReportsQuery(
    params.id,
    canReport && !isPlatformAdmin,
  );
  const reports = isPlatformAdmin ? adminReports : myReports;
  const hasReportsTab = isPlatformAdmin || canReport;
  // The admin list holds everyone's reports; only the viewer's own can lock their button.
  const hasOpenOwnReport = (
    isPlatformAdmin
      ? adminReports.filter(({ reporterId }) => reporterId === session?.id)
      : myReports
  ).some(isOpenProviderReport);

  if (!serviceProvider) {
    return (
      <BasicLayout>
        <Header title={<Breadcrumb {...breadcrumb} />} actionsWidth={44} />
      </BasicLayout>
    );
  }

  const id = serviceProvider.id;
  const images = serviceProvider.images ?? [];

  const canAddNotes = !isServiceProvider;
  // A catalogue entry is a card this vessel typed out — there is no account
  // behind it to open a chat with.
  const canChat = serviceProvider.created_by !== ServiceProviderCreatedBy.VESSEL;
  // The one category the aside names them by; the Services card lists the rest.
  const category = providerCategoryTitles(serviceProvider)[0];

  const editRecord = canModify
    ? () => setOpenedModal(OpenedModal.EDIT_SERVICE_PROVIDER)
    : undefined;
  const editNote = canAddNotes ? () => setOpenedModal(OpenedModal.UPDATE_NOTE) : undefined;

  // Editing happens where the values are read, which leaves one act belonging
  // to no card. It is the row My Profile ends its own list with — the only red
  // thing on the page, and found the same way.
  const deleteAction = canModify && (
    <ProfileNav
      ariaLabel="Service Provider Actions"
      items={[
        {
          title: 'Delete Service Provider',
          sub: 'Permanently delete this service provider.',
          icon: <BiTrash />,
          danger: true,
          onClick: () => setOpenedModal(OpenedModal.DELETE_SERVICE_PROVIDER),
        },
      ]}
      onSelect={() => {}}
    />
  );

  // The same section rows the vessel profile switches its cards with.
  const openReports = reports.filter(isOpenProviderReport).length;
  const tabs = hasReportsTab && (
    <ProfileNav
      ariaLabel="Service Provider Sections"
      mobileLayout="pills"
      active={tab}
      onSelect={(next) => setTab(next as ProviderTab)}
      items={[
        {
          id: 'info',
          title: 'Info',
          sub: 'About, services and contacts.',
          icon: <BiInfoCircle />,
        },
        {
          id: 'reports',
          title: 'Reports',
          sub:
            reports.length === 0
              ? 'Nothing reported'
              : [
                  `${reports.length} report${reports.length === 1 ? '' : 's'}`,
                  openReports > 0 && `${openReports} open`,
                ]
                  .filter(Boolean)
                  .join(' · '),
          icon: <BiFlag />,
        },
      ]}
    />
  );
  const showReports = hasReportsTab && tab === 'reports';
  const report = canReport ? () => setOpenedModal(OpenedModal.REPORT_SERVICE_PROVIDER) : undefined;

  const renderModal = () => {
    switch (openedModal) {
      case OpenedModal.UPDATE_NOTE: {
        return (
          <UpdateNote
            id={id}
            onClose={() => setOpenedModal(OpenedModal.NONE)}
            initialNote={serviceProvider.note}
          />
        );
      }
      case OpenedModal.EDIT_SERVICE_PROVIDER: {
        return (
          <CreateUpdateServiceProvider
            initialData={serviceProvider}
            onClose={() => setOpenedModal(OpenedModal.NONE)}
            onSuccess={() => setOpenedModal(OpenedModal.NONE)}
          />
        );
      }
      case OpenedModal.REPORT_SERVICE_PROVIDER: {
        return (
          <ReportProviderModal
            id={id}
            name={serviceProvider.name}
            onClose={() => setOpenedModal(OpenedModal.NONE)}
          />
        );
      }
    }
  };

  return (
    <BasicLayout>
      <Header title={<Breadcrumb {...breadcrumb} />} actionsWidth={44} />
      <BasicLayoutContent>
        <SplitLayout
          stacked
          aside={
            <aside className={d.aside}>
              <div className={s.identityWrap}>
                <ProfileIdentity
                  name={serviceProvider.name}
                  avatar={imageVariantLink(serviceProvider.logo, 96)}
                  /* A catalogue entry is this vessel's own card of someone, not an
                     account they keep — worth saying on a page that otherwise reads
                     like one, and said where the name is read. */
                  tag={
                    canModify && serviceProvider.created_by === ServiceProviderCreatedBy.VESSEL ? (
                      <p className={clsx('body-small', d.muted)}>Added by this vessel</p>
                    ) : null
                  }
                >
                  <div className={s.identityBody}>
                    {/* Sky blue, as the design names the category: the teal tones are
                        the buttons' under it, and a pill that matched them read as one. */}
                    {!!category && <StatusPill label={category} tone="progress" />}
                    {!!serviceProvider.short_description && (
                      <p className={s.tagline}>{serviceProvider.short_description}</p>
                    )}

                    <div className={s.reach}>
                      {/* Calling, writing and opening their site are the values
                          themselves, linked — the old Call, Email and Site buttons. */}
                      <RecordContactList
                        contact={serviceProvider.contact_info}
                        place={[providerAddress(serviceProvider), providerDistance(serviceProvider)]
                          .filter(Boolean)
                          .join(' · ')}
                      />
                      {(!isServiceProvider || canChat) && (
                        <div className={s.identityActions}>
                          {!isServiceProvider && (
                            <TaskAction
                              creatorId={serviceProvider.creator_id}
                              creatorName={serviceProvider.name}
                            />
                          )}
                          {canChat && (
                            <ButtonOutlinedSmall onClick={() => void handleCreateChat()}>
                              <BiChat aria-hidden="true" />
                              <span className="body-medium">Contact</span>
                            </ButtonOutlinedSmall>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </ProfileIdentity>
                <span className={s.shortlistCorner}>
                  <ShortlistButton
                    iconOnly
                    favorite={serviceProvider.favorite}
                    disabled={isSetIsFavoritePending}
                    onToggle={() =>
                      void setIsFavorite({
                        vesselId: getServiceId(),
                        id,
                        favorite: !serviceProvider.favorite,
                      })
                    }
                  />
                </span>
              </div>

              {tabs}
              {!folded && deleteAction}
            </aside>
          }
        >
          {showReports ? (
            <section className={d.detail}>
              <ProviderReportsTab
                reports={reports}
                canChangeStatus={isPlatformAdmin}
                onReport={report}
                reportLocked={hasOpenOwnReport}
              />
            </section>
          ) : (
            <section className={d.detail}>
              <RecordSection title="About" onEdit={editRecord}>
                <RecordText value={serviceProvider.description} empty="No description provided." />
              </RecordSection>

              <RecordSection title="Services">
                <RecordServices locations={serviceProvider.locations} />
              </RecordSection>

              <RecordSection title="Locations">
                <RecordLocations locations={serviceProvider.locations} />
              </RecordSection>

              <RecordSection title="Contact">
                <RecordContactFacts contact={serviceProvider.contact_info} />
              </RecordSection>

              {images.length > 0 && (
                <RecordSection title="Past Work">
                  <RecordPhotos images={images} onPreview={setPreviewImage} />
                </RecordSection>
              )}

              {/* Yours, not theirs — whatever this vessel wrote about working with
                  them. Hidden from a provider reading their own page: the note is
                  the vessel's private record of them. */}
              {canAddNotes && (
                <RecordSection title="Notes" onEdit={editNote}>
                  <RecordText value={serviceProvider.note} empty="No notes yet." />
                </RecordSection>
              )}
            </section>
          )}

          {/* Folded, the aside stacks above the record, which would leave the
              one destructive act right under the contact buttons — there it ends
              the page instead. */}
          {folded && deleteAction}
        </SplitLayout>
      </BasicLayoutContent>

      {renderModal()}
      <ConfirmationModal
        open={openedModal === OpenedModal.DELETE_SERVICE_PROVIDER}
        onClose={() => setOpenedModal(OpenedModal.NONE)}
        onConfirm={() => void handleDelete()}
        title="Delete Service Provider"
        message="Would you like to delete this service provider and everything on it? You can’t undo this."
        confirmText={isDeleting ? 'Deleting…' : 'Delete'}
      />
      {previewImage && (
        <DocumentPreviewModal
          document={{
            contentType: previewImage.content_type ?? 'image/*',
            name: previewImage.name,
            storageLink: previewImage.link,
          }}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </BasicLayout>
  );
};

export default SingleServiceProvider;

enum OpenedModal {
  NONE = 'NONE',
  UPDATE_NOTE = 'UPDATE_NOTE',
  EDIT_SERVICE_PROVIDER = 'EDIT_SERVICE_PROVIDER',
  DELETE_SERVICE_PROVIDER = 'DELETE_SERVICE_PROVIDER',
  REPORT_SERVICE_PROVIDER = 'REPORT_SERVICE_PROVIDER',
}

type ProviderTab = 'info' | 'reports';

const schema = z.object({
  note: z.string(),
});

type AddNoteFormValues = z.infer<typeof schema>;

const UpdateNote = ({
  onClose,
  id,
  initialNote,
}: {
  id: string;
  onClose: () => void;
  initialNote?: null | string;
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AddNoteFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      note: initialNote ?? '',
    },
    mode: 'onChange',
  });

  const { mutateAsync: addNote } = useServiceProviderUpdateNoteMutation();
  const { mutateAsync: deleteNote } = useServiceProviderDeleteNoteMutation();

  const handleNoteAdd = async ({ note }: AddNoteFormValues) => {
    const vesselId = getServiceId();

    if (!note.trim()) {
      if (!initialNote) {
        onClose();
        return;
      }
      try {
        await deleteNote({ id, vesselId });
        toast.success('Note deleted');
        onClose();
      } catch (err: unknown) {
        console.error(err);
        toast.error("Couldn't delete the note");
      }
      return;
    }

    try {
      await addNote({
        id,
        vesselId,
        note,
      });
      toast.success(`Note ${initialNote ? 'updated' : 'created'}`);
      onClose();
    } catch (err: unknown) {
      console.error(err);
      toast.error(`Couldn't ${initialNote ? 'update' : 'create'} the note`);
    }
  };

  return (
    <Modal open title={initialNote ? 'Edit note' : 'Add note'} onClose={onClose}>
      <form className={clsx(s.ModalContent, 'body-large')} onSubmit={handleSubmit(handleNoteAdd)}>
        <Controller
          name="note"
          control={control}
          render={({ field }) => (
            <RichTextArea
              label="Description"
              placeholder="Description"
              value={field.value ?? ''}
              onChange={field.onChange}
              error={errors.note?.message}
            />
          )}
        />
        <Button type="submit" disabled={isSubmitting}>
          {initialNote ? 'Edit' : 'Add'}
        </Button>
      </form>
    </Modal>
  );
};

/**
 * Requesting service from them. Which act that is depends on where you arrived
 * from: with a task in the URL the provider joins that task, otherwise a new one
 * is written here.
 */
const TaskAction = (props: { creatorId?: string; creatorName?: string }) => {
  const [searchParams] = useSearchParams();

  const taskIdToAssign = searchParams.get(SERVICE_PROVIDER_ASSIGN_TO_SEARCH_KEY);

  if (taskIdToAssign) {
    return <AssignTask {...props} />;
  }

  return <CreateTask {...props} />;
};

const CreateTask = ({ creatorId, creatorName }: { creatorId?: string; creatorName?: string }) => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { createTask, setParticipant } = useTasks();
  const { createActivityGenerator } = useVessels();
  const postBid = useChats((state) => state.postBid);
  const [show, setShow] = useState(false);

  const equipmentId = searchParams.get(EQUIPMENT_ID_SEARCH_KEY);
  const equipmentName = searchParams.get(EQUIPMENT_NAME_SEARCH_KEY);

  // A failure is left to reach the task wizard: it keeps what was entered and says so.
  const onSubmitTask = async (payload: TaskSubmissionPayload) => {
    if (payload.type === 'repeatable') {
      await createActivityGenerator(payload.data);
      setShow(false);
    } else {
      const { id } = await createTask(payload.data);
      const spUserId = creatorId ?? params.id!;
      await setParticipant(id, spUserId, creatorName);
      await postBid(params.id!, id, null);
      // A task bound to equipment is the end of the request-service flow that started
      // on that item, so it lands on the item's service history rather than the
      // maintenance list — filtered to the equipment, which arrives as a visible chip.
      // Without equipment there is no history to land in, so it stays on Tasks.
      const requestedEquipmentId = payload.data.equipment?.id;
      navigate(
        requestedEquipmentId
          ? `${SERVICE_HISTORY_ROUTE}?${EQUIPMENT_ID_SEARCH_KEY}=${requestedEquipmentId}`
          : `/tasks?${TASK_ID_SEARCH_PARAM}=${id}`,
      );
    }
  };

  return (
    <>
      <ButtonSmall onClick={() => setShow(true)}>
        <BiWrench aria-hidden="true" />
        <span className="body-medium">Request service</span>
      </ButtonSmall>
      {show && (
        <CreateEditTaskSheet
          open
          onClose={() => setShow(false)}
          onSubmit={onSubmitTask}
          title="Add Task"
          assigneeId={creatorId}
          assigneeName={creatorName}
          equipment={equipmentId && equipmentName ? { id: equipmentId, name: equipmentName } : null}
          showCost
          hidePriority
        />
      )}
    </>
  );
};

const AssignTask = ({ creatorId, creatorName }: { creatorId?: string; creatorName?: string }) => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getTaskById, currentTask, setParticipant } = useTasks();
  const postBid = useChats((state) => state.postBid);

  const taskId = searchParams.get(SERVICE_PROVIDER_ASSIGN_TO_SEARCH_KEY);

  useEffect(() => {
    void getTaskById(taskId!);
  }, [taskId, getTaskById]);

  const createBid = async () => {
    if (!currentTask) return;
    const spUserId = creatorId ?? params.id!;
    const alreadyAdded = currentTask.participants?.some(
      (participant) => participant.person_id === spUserId,
    );

    if (alreadyAdded) {
      toast.info('This provider is already on this task.');
    } else {
      // The task itself is left alone: requesting service adds the provider beside
      // whoever already owns the task, it doesn't hand the task over. The assignee stays
      // put, so the task keeps its place in the assignee cut on the tasks list — someone
      // on the crew is still answerable for it while the bid is out.
      //
      // Nothing here may write `responsible`: Activity validates `Participant.Name` as
      // required, and `{ person_id, type }` — all this site can build for a provider —
      // fails the update. Naming the provider as responsible is what broke this flow
      // before (2f49011b).
      void setParticipant(taskId!, spUserId, creatorName);
      await postBid(params.id!, taskId!, null);
    }

    // Back to the task, on whichever list owns it — the provider was just added
    // to an existing task, so its kind is whatever it already was.
    navigate(taskHref(currentTask));
  };

  return (
    <ButtonSmall onClick={createBid} disabled={!currentTask}>
      <FaRegHandshake aria-hidden="true" />
      <span className="body-medium">Add to task</span>
    </ButtonSmall>
  );
};
